import { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { EmployeeProfile } from '@/types/nib/employeeProfile';
import { ProfileCard } from './ProfileCard';

interface VirtualizedProfileListProps {
  profiles: EmployeeProfile[];
  search?: string;
  isLoading?: boolean;
  deletingId: number | null;
  onDelete: (profile: EmployeeProfile) => void;
}

export interface VirtualizedProfileListRef {
  scrollToTop: () => void;
}

export const VirtualizedProfileList = forwardRef<
  VirtualizedProfileListRef,
  VirtualizedProfileListProps
>(({ profiles, search, isLoading, deletingId, onDelete }, ref) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: profiles.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  useImperativeHandle(ref, () => ({
    scrollToTop: () =>
      parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' }),
  }));

  // Reset scroll when search changes
  useEffect(() => {
    parentRef.current?.scrollTo({ top: 0 });
  }, [search]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-20 bg-gray-200 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!profiles.length) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <svg
          className="mx-auto h-16 w-16 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        <p className="text-gray-600 font-medium mt-4">
          {search ? 'No employees match your search' : 'No saved profiles yet'}
        </p>
        <p className="text-gray-500 text-sm mt-1">
          {search
            ? 'Try different search terms or clear filters'
            : 'Add employees in Manual Entry and check "Save as profile"'}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className="h-[calc(100vh-340px)] min-h-[400px] overflow-auto"
      style={{ contain: 'strict' }}
    >
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((row) => {
          const profile = profiles[row.index]!;
          return (
            <div
              key={profile.id}
              className="absolute top-0 left-0 w-full"
              style={{
                height: row.size,
                transform: `translateY(${row.start}px)`,
                paddingBottom: 12,
              }}
            >
              <ProfileCard
                profile={profile}
                search={search}
                isDeleting={deletingId === profile.id}
                onDelete={() => onDelete(profile)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

VirtualizedProfileList.displayName = 'VirtualizedProfileList';
